import React, { useRef, useState } from 'react';
import { useScrollReveal } from '../../hooks/useScrollReveal';
import './ImageReveal.css';

export type AspectRatio = '16:9' | '4:3' | '1:1' | 'auto';

export interface ImageRevealProps {
  /** Image source URL */
  src: string;
  /** Image alt text (required for accessibility) */
  alt: string;
  /** Aspect ratio for the container */
  aspectRatio?: AspectRatio;
  /** Title attribute for additional context */
  title?: string;
  /** Whether to show an overlay (useful for text on image) */
  overlay?: boolean;
  /** Overlay content (text, icons, etc.) */
  overlayContent?: React.ReactNode;
  /** Whether the image should pause the animation on hover */
  pauseOnHover?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Scroll reveal threshold (0-1) */
  revealThreshold?: number;
}

/**
 * Image component with smooth scroll reveal animation.
 * Features scale + fade effect that triggers when scrolling into view.
 * Perfect for portfolio images and visual storytelling.
 *
 * @example
 * <ImageReveal
 *   src="/images/project.jpg"
 *   alt="Painting project"
 *   aspectRatio="16:9"
 * />
 *
 * <ImageReveal
 *   src="/images/banner.jpg"
 *   alt="Storefront"
 *   aspectRatio="4:3"
 *   overlay
 *   overlayContent={<h3>Custom Painting Services</h3>}
 * />
 */
export const ImageReveal: React.FC<ImageRevealProps> = ({
  src,
  alt,
  aspectRatio = 'auto',
  title,
  overlay = false,
  overlayContent,
  pauseOnHover = true,
  className = '',
  revealThreshold = 0.15,
}) => {
  const containerRef = useScrollReveal({
    threshold: revealThreshold,
    rootMargin: '0px 0px -50px 0px',
  });

  const imageRef = useRef<HTMLImageElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  const containerClassName = `image-reveal image-reveal--${aspectRatio} ${
    overlay ? 'image-reveal--has-overlay' : ''
  } ${className}`;

  const imageClassName = `image-reveal__image ${
    pauseOnHover && isHovered ? 'image-reveal__image--paused' : ''
  } ${!isLoaded ? 'image-reveal__image--loading' : ''}`;

  return (
    <div
      ref={containerRef}
      className={containerClassName}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="image-reveal__wrapper">
        <img
          ref={imageRef}
          src={src}
          alt={alt}
          title={title}
          className={imageClassName}
          loading="lazy"
          decoding="async"
          onLoad={() => setIsLoaded(true)}
        />

        {overlay && (
          <div className="image-reveal__overlay">
            {overlayContent && (
              <div className="image-reveal__content">
                {overlayContent}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

ImageReveal.displayName = 'ImageReveal';
